import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Ban, ChevronRight, Flag, Globe, MapPin, Radio, Search, Video, Crown, VenetianMask } from "lucide-react";
import MemberActionsMenu from "@/components/social-meet/MemberActionsMenu";
import BlockConfirmDialog from "@/components/BlockConfirmDialog";
import ReportModal from "@/components/ReportModal";
import { toast } from "sonner";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";

function lastActive(iso) {
  if (!iso) return "A while ago";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5 animate-pulse" data-testid="member-live-badge">
      <Radio size={9} /> LIVE
    </span>
  );
}

function AnonCard({ m, onRemove }) {
  const [blockOpen, setBlockOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <div data-testid={`anon-card-${m.anon_id}`} className="card-surface p-4 flex flex-col gap-3">
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} target={{ type: "user", id: m.anon_id }} />
      <BlockConfirmDialog open={blockOpen} onOpenChange={setBlockOpen} targetRef={m.anon_id} name={m.alias} onBlocked={onRemove} testid={`anon-block-${m.anon_id}`} />
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
          <VenetianMask size={20} className="text-rose-400" />
        </div>
        <div>
          <div className="text-sm font-medium">{m.alias}</div>
          <span className={`text-[10px] uppercase tracking-widest ${m.online ? "text-emerald-400" : "text-zinc-500"}`}>
            {m.online ? "Online now" : "Anonymous member"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-auto">
        <Button data-testid={`anon-block-${m.anon_id}`} onClick={(e) => { e.stopPropagation(); setBlockOpen(true); }} variant="ghost" className="flex-1 rounded-none border border-white/10 text-xs h-9 hover:bg-white/5" aria-label={`Block ${m.alias}`}>
          <Ban size={13} className="mr-1.5" /> Block
        </Button>
        <Button data-testid={`anon-report-${m.anon_id}`} onClick={(e) => { e.stopPropagation(); setReportOpen(true); }} variant="ghost" className="flex-1 rounded-none border border-white/10 text-xs h-9 hover:bg-white/5" aria-label={`Report ${m.alias}`}>
          <Flag size={13} className="mr-1.5" /> Report
        </Button>
      </div>
    </div>
  );
}

export function MemberCard({ m, onFollow, onRemove, onLeave }) {
  const navigate = useNavigate();
  const liveTo = m.live_is_community && m.live_community_id
    ? `/c/${m.live_community_id}/cam`
    : m.live_stream_id ? `/live?s=${m.live_stream_id}` : null;
  const remember = () => { try { sessionStorage.setItem("explore:return", String(Date.now())); } catch { /* ignore */ } onLeave?.(); };
  return (
    <div data-testid={`member-card-${m.id}`} className="group relative aspect-[3/4] overflow-hidden rounded-xl bg-zinc-900 border border-white/10 hover-lift">
      {/* Photo tile — avatar fills the card (reference design); initials when missing */}
      <Link to={`/u/${m.id}`} data-testid={`member-view-${m.id}`} onClick={remember} className="absolute inset-0 block" aria-label={`Open ${m.display_name}'s profile`}>
        {m.avatar_url ? (
          <img src={m.avatar_url} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rose-900/60 to-zinc-900 text-4xl font-display text-white/80">{m.display_name?.[0]?.toUpperCase()}</div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      </Link>

      {/* Top-left badges: LIVE / Creator / Featured / New — compact, never over the face center */}
      <div className="absolute top-1.5 left-1.5 right-1.5 flex flex-wrap gap-1 pointer-events-none">
        {m.is_live && liveTo && (
          <button type="button" data-testid={`member-live-${m.id}`} onClick={(e) => { e.stopPropagation(); e.preventDefault(); remember(); navigate(liveTo); }} aria-label={`Watch ${m.display_name} live`} className="pointer-events-auto"><LiveBadge /></button>
        )}
        {m.is_creator && <span className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-widest bg-black/60 backdrop-blur text-rose-300 px-1.5 py-0.5 rounded-sm"><Video size={8} /> Creator</span>}
        {m.referral_featured && <span className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-widest bg-amber-400/90 text-black px-1.5 py-0.5 rounded-sm"><Crown size={8} /> Featured</span>}
        <LifetimeBossBadge show={m.lifetime_boss} size="xs" />
        {m.is_new && !m.is_live && <span className="text-[8px] uppercase tracking-widest bg-sky-500/80 text-white px-1.5 py-0.5 rounded-sm">New</span>}
      </div>

      {/* Bottom chip: online dot · name · distance — chevron opens the profile */}
      <div className="absolute inset-x-0 bottom-0 p-2 flex items-end justify-between gap-1 pointer-events-none">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span data-testid={`member-status-${m.id}`} className={`w-2 h-2 rounded-full shrink-0 ${m.online ? "bg-emerald-400" : "bg-zinc-500"}`} title={m.online ? "Online" : "Offline"} />
            <span className="text-xs sm:text-sm font-semibold text-white truncate drop-shadow">{m.display_name}</span>
          </div>
          <div className="text-[11px] text-zinc-300/90 truncate pl-3.5">
            {m.display_name}{m.distance_miles != null ? <span className="inline-flex items-center gap-0.5 ml-1"><MapPin size={8} /> ~{m.distance_miles} mi</span> : null}
          </div>
        </div>
        <Link to={`/u/${m.id}`} onClick={remember} data-testid={`member-open-${m.id}`} aria-label={`View ${m.display_name}`} className="pointer-events-auto w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center shrink-0 hover:bg-white"><ChevronRight size={15} /></Link>
      </div>

      {/* Stable action menu (top-right) — replaces the invisible centre overlay that swallowed taps */}
      <MemberActionsMenu m={m} onFollow={onFollow} onRemove={onRemove} onLeave={onLeave} />
    </div>
  );
}

const RESTORE_TTL_MS = 10 * 60 * 1000;
const restoreKey = (filter) => `sm:grid:${filter}`;
function readSnapshot(filter) {
  try {
    const raw = sessionStorage.getItem(restoreKey(filter));
    if (!raw) return null;
    const snap = JSON.parse(raw);
    if (!snap || Date.now() - snap.at > RESTORE_TTL_MS) return null;
    return snap;
  } catch { return null; }
}

export default function MembersGrid({ filter }) {
  const snap = useRef(readSnapshot(filter));
  const [members, setMembers] = useState(snap.current?.members || []);
  const [q, setQ] = useState(snap.current?.q || "");
  const [page, setPage] = useState(snap.current?.page || 1);
  const [hasMore, setHasMore] = useState(!!snap.current?.hasMore);
  const [loading, setLoading] = useState(!snap.current);
  const stateRef = useRef({});
  const leaveRef = useRef(null);
  stateRef.current = { members, q, page, hasMore };
  useEffect(() => {
    // Restore scroll once the restored cards are in the DOM (retry a few
    // frames — on phones the layout settles after fonts/avatars arrive).
    const targetY = snap.current?.scrollY;
    let tries = 0;
    const restore = () => {
      if (!targetY) return;
      window.scrollTo(0, targetY);
      if (Math.abs(window.scrollY - targetY) > 4 && tries++ < 20) setTimeout(restore, 50);
    };
    if (targetY) requestAnimationFrame(restore);
    // Track the live scroll offset — by unmount time the router may already
    // have swapped in a shorter page and clamped window.scrollY to 0.
    let lastY = targetY || window.scrollY;
    let written = false; // the card click writes the snapshot synchronously; cleanup must not overwrite it
    const write = (y) => {
      const st = stateRef.current;
      try {
        sessionStorage.setItem(restoreKey(filter), JSON.stringify({ ...st, members: st.members.slice(0, 240), scrollY: y, at: Date.now() }));
      } catch { /* quota */ }
    };
    const onScroll = () => { if (!written) lastY = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    leaveRef.current = () => { written = true; write(window.scrollY); };
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (!written) write(lastY);
    };
  }, [filter]);
  const [error, setError] = useState(false);
  const [meAnon, setMeAnon] = useState(null);
  const sentinelRef = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(async (pageNum, query, append) => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/members", { params: { q: query, filter, page: pageNum, limit: 24 } });
      setMembers((prev) => (append ? [...prev, ...data.members] : data.members));
      setHasMore(data.has_more);
      if (data.me_anonymous !== undefined) setMeAnon(!!data.me_anonymous);
    } catch {
      setError(true);
      toast.error("Couldn't load members.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (snap.current) { snap.current = null; return; } // restored from snapshot — skip the reload that would reset scroll
    setPage(1); load(1, q, false);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // a block was undone from the toast → bring the card back
  useEffect(() => {
    const h = () => load(1, q, false);
    window.addEventListener("members:reload", h);
    return () => window.removeEventListener("members:reload", h);
  }, [q, load]);

  // gentle live/online freshness — refresh page 1 every 30s, no aggressive polling
  useEffect(() => {
    if (!["live", "online"].includes(filter)) return;
    const t = setInterval(() => load(1, q, false), 30000);
    return () => clearInterval(t);
  }, [filter, q, load]);

  const applySearch = (value) => {
    setQ(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(1, value, false); }, 300);
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        const next = page + 1;
        setPage(next);
        load(next, q, true);
      }
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, page, q, load]);

  const onFollow = async (m) => {
    const was = m.is_following;
    setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_following: !was } : x)));
    try {
      if (was) await api.delete(`/users/${m.id}/follow`);
      else await api.post(`/users/${m.id}/follow`);
    } catch {
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_following: was } : x)));
      toast.error("Action failed. Try again.");
    }
  };

  const removeCard = (key) => setMembers((prev) => prev.filter((x) => (x.anon_id || x.id) !== key));

  const toggleAnon = async () => {
    try {
      const { data } = await api.put("/users/me/anonymous", { enabled: !meAnon });
      setMeAnon(data.enabled);
      toast.success(data.enabled ? `You appear here as "${data.alias}".` : "You no longer appear in Anonymous.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };

  return (
    <div data-testid={`social-meet-grid-${filter}`}>
      {filter === "anon" && meAnon !== null && (
        <div className="card-surface p-4 mb-4 flex items-center justify-between gap-3" data-testid="anon-optin-card">
          <div className="text-sm text-zinc-300">
            <VenetianMask size={14} className="inline mr-1.5 text-rose-400" />
            Appear here anonymously — others see only a "Panda ####" alias, never your profile.
          </div>
          <Button data-testid="anon-optin-toggle" onClick={toggleAnon} variant="ghost" className={`rounded-none border text-xs h-9 ${meAnon ? "border-rose-600/60 text-rose-300" : "border-white/10 text-zinc-400"}`}>
            {meAnon ? "On" : "Off"}
          </Button>
        </div>
      )}
      {filter !== "anon" && (
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="members-search-input" value={q} onChange={(e) => applySearch(e.target.value)} placeholder="Explore more…" className="pl-9 bg-zinc-950 border-white/10 rounded-none h-11" />
        </div>
      )}

      {error && !loading ? (
        <div data-testid="members-error" className="card-surface p-8 text-center text-sm text-zinc-400">
          Something went wrong. <button type="button" className="text-rose-400 underline" onClick={() => load(1, q, false)}>Try again</button>
        </div>
      ) : members.length === 0 && !loading ? (
        <div data-testid="members-empty" className="card-surface p-8 text-center text-sm text-zinc-500">
          {filter === "live" ? "Nobody you can watch is live right now." : filter === "new_nearby" ? "No new members near you yet — Global has everyone." : filter === "anon" ? "No anonymous members yet — be the first to switch it on." : `No members match${q ? ` “${q}”` : ""}.`}
        </div>
      ) : (
        <>
        <div className="flex items-center justify-between mb-3" data-testid="members-section-header">
          <h2 className="font-display text-xl sm:text-2xl flex items-center gap-2">
            {filter === "all" ? <>Global <Globe size={18} className="text-amber-400" /></> : filter === "new_nearby" ? <>New &amp; Nearby <MapPin size={18} className="text-rose-400" /></> : filter === "live" ? <>Live Now <Radio size={18} className="text-red-500" /></> : filter === "online" ? "Online" : "Anonymous"}
          </h2>
          <span className="text-xs text-zinc-500">{members.length}{hasMore ? "+" : ""} members</span>
        </div>
        <div data-testid="members-grid-3col" className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
          {members.map((m) => m.is_anonymous
            ? <AnonCard key={m.anon_id} m={m} onRemove={() => removeCard(m.anon_id)} />
            : <MemberCard key={m.id} m={m} onFollow={onFollow} onRemove={() => removeCard(m.id)} onLeave={() => leaveRef.current?.()} />)}
        </div>
        </>
      )}

      {loading && (
        <div data-testid="members-loading" className="text-center text-xs text-zinc-500 py-8 uppercase tracking-widest">Loading members…</div>
      )}
      <div ref={sentinelRef} className="h-2" />
    </div>
  );
}
